/**
 * The splash, once per visit, in front of the chooser or the menu (ORDR-164).
 *
 * `target.level === "brand"`: the brand's message before the chooser.
 * `target.level === "location"`: once a location is chosen, its message before
 * the menu. The location read returns `[brand, location]`; the brand's item is
 * shown here only when `brandNodeId` names the brand and its message has not
 * been seen this visit (a diner who arrived by a QR straight to a location),
 * and dismissing marks it seen too, so it never stands in front of the chooser
 * afterwards.
 *
 * NEVER A BLOCK. Already seen: the children render at once and nothing is
 * read. Otherwise the read is awaited for at most `waitMs`; an empty list, a
 * failure (`readSplash` returns `[]` for every one) or no answer in time all
 * render the children. Only a message the diner has not seen stands in front.
 */

import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from "react";
import type { ApiClient } from "../api/client.js";
import { Splash } from "./Splash.js";
import { readSplash, type SplashMessage, type SplashTarget } from "./splashApi.js";
import { browserSessionStorage, createSplashSeenStore } from "./splashSeen.js";

export interface SplashGateProps {
  readonly client: ApiClient;
  readonly target: SplashTarget;
  /** The brand above a location target, when the address names it. */
  readonly brandNodeId?: string;
  /** Injected for tests. Defaults to this tab's sessionStorage. */
  readonly storage?: () => Storage | null;
  /** How long a splash read may hold the screen before it gives way. */
  readonly waitMs?: number;
  readonly children: ReactNode;
}

export const SPLASH_WAIT_MS = 2500;

type Phase =
  | { readonly kind: "waiting" }
  | { readonly kind: "showing"; readonly messages: readonly SplashMessage[] }
  | { readonly kind: "through" };

export function SplashGate({
  client,
  target,
  brandNodeId,
  storage = browserSessionStorage,
  waitMs = SPLASH_WAIT_MS,
  children,
}: SplashGateProps): ReactElement {
  const store = useMemo(() => createSplashSeenStore(storage), [storage]);
  const { level, nodeId } = target;
  const [phase, setPhase] = useState<Phase>(() =>
    store.seen(level, nodeId) ? { kind: "through" } : { kind: "waiting" },
  );
  const waiting = phase.kind === "waiting";

  useEffect(() => {
    if (!waiting) {
      return undefined;
    }
    let live = true;
    const giveWay = setTimeout(() => {
      if (live) setPhase({ kind: "through" });
    }, waitMs);
    void readSplash(client, { level, nodeId }).then((all) => {
      if (!live) return;
      clearTimeout(giveWay);
      const unseen = all.filter((message) =>
        level === "brand"
          ? true
          : message.level !== "BRAND" || (brandNodeId !== undefined && !store.seen("brand", brandNodeId)),
      );
      setPhase(unseen.length === 0 ? { kind: "through" } : { kind: "showing", messages: unseen });
    });
    return () => {
      live = false;
      clearTimeout(giveWay);
    };
  }, [waiting, client, level, nodeId, brandNodeId, store, waitMs]);

  if (phase.kind === "through") {
    return <>{children}</>;
  }
  if (phase.kind === "waiting") {
    return (
      <p role="status" data-testid="splash-loading" className="sr-only">
        Loading
      </p>
    );
  }
  return (
    <Splash
      messages={phase.messages}
      actionLabel={level === "brand" ? "Continue" : "Start your order"}
      onContinue={() => {
        store.mark(level, nodeId);
        if (brandNodeId !== undefined && phase.messages.some((message) => message.level === "BRAND")) {
          store.mark("brand", brandNodeId);
        }
        setPhase({ kind: "through" });
      }}
    />
  );
}
